import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import TwField, { TwInputTypes } from './../../../components/TwFields/TwFields';
import NumberUtils from './../../../utils/numberUtils';

import InfoIcon from '@mui/icons-material/Info';
import TwTooltip from '../../TwTooltip/TwTooltip';
import { ReactComponent as CalculatorIcon } from './../../../assets/icons/calculator.svg';

import { LOAD_TYPE } from '../../../redux/api/load/load.model';
import LoadsService from '../../../providers/Loads/Loads.service';
import { useAppSelector } from '../../../redux/hooks';
import OtherLoadPay, { OtherPayErrors } from '../OtherLoadPay/OtherLoadPay';
import useFeatureFlags from '../../../providers/FeatureFlags/FeatureFlags.provider';
import useAuth from '../../../providers/Auth/Auth.provider';
import { MIN_STOP_COUNT, SliceTrip } from '../../../redux/features/orders/ordersSlice';
import { TripDriverOtherPay, TripDriverOverrideData } from '../../../redux/api/trips/trips.model';
import { EntityType } from '../../../redux/Global.model';
import { useGetTenantRoutingSettingsQuery } from '../../../redux/api/tenant/tenantApi';
import { PROVIDER } from '../../../providers/User/User.model';

export interface CalculationBoxProps {
  grossPayEstimateData: TripDriverOverrideData | undefined;
  onGrossPayEstimateChange: (grossPay: TripDriverOverrideData) => void;
  onOtherPayItemsChange: (otherLoadPay: TripDriverOtherPay[]) => void;
  isModalOpen: boolean;
  hasBeenReset: boolean;
  handleResetValues: (reset: boolean) => void;
  splitLoadSegment?: SliceTrip;
  driversNumber: number;
  otherLoadPayItems: TripDriverOtherPay[];
  otherPayItemsErrors?: OtherPayErrors[];
  entity: EntityType;
}

const CalculationBox: React.FC<CalculationBoxProps> = ({
  grossPayEstimateData,
  onGrossPayEstimateChange,
  onOtherPayItemsChange,
  isModalOpen,
  hasBeenReset,
  handleResetValues,
  splitLoadSegment,
  driversNumber,
  otherLoadPayItems,
  otherPayItemsErrors,
  entity
}) => {
  const {
    getTenantSetting,
    data: { userData }
  } = useAuth();
  const { enableOtherPayInDriverPayModal, enableHideGoogleMapsAndRoutingCalculations } =
    useFeatureFlags();
  const enableLtl = userData?.tenant?.ltl_enabled || false;
  const isDriverPayPercentageBasedOnDefaultService = getTenantSetting(
    'driver-pay-percentage-based-on-default-service'
  );
  const isMileageAutoCalculationDisabled = getTenantSetting('disable-auto-calculate-mileage');

  const [totalBaseAndOtherPay, setTotalBaseAndOtherPay] = useState<number>(0);
  const [grossPayEstimate, setGrossPayEstimate] = useState<number>(0); // Base pay, excluding other pay items
  const [payRate, setPayRate] = useState<number>(grossPayEstimateData?.rate ?? 0);

  const { order_type: load_type, provider_stops } = useAppSelector((state) => state.orders);
  const { provider_trip_stops } = useAppSelector((state) => state.trip);
  // By per mile
  const [loadedMiles, setLoadedMiles] = useState<number>(grossPayEstimateData?.loaded_miles ?? 0);
  const [deadheadMiles, setDeadheadMiles] = useState<number>(
    grossPayEstimateData?.empty_miles ?? 0
  );

  // By percentage
  const [loadSegmentRevenue, setLoadSegmentRevenue] = useState<number>(
    grossPayEstimateData?.trip_revenue_percentage ?? 0
  );

  const [loadRate, setLoadRate] = useState<number>(grossPayEstimateData?.overriden_order_rate ?? 0);
  const [manuallySetLoadRate, setManuallySetLoadRate] = useState<boolean>(false);

  // By hour
  const [quantity, setQuantity] = useState<number>(grossPayEstimateData?.quantity ?? 0);

  // Flat
  const [flatRate, setFlatRate] = useState<number>(grossPayEstimateData?.gross_pay_estimate ?? 0);

  // Other pay items
  const [otherLoadPayTotal, setOtherLoadPayTotal] = useState<number>(0);

  const [milesTooltip, setMilesTooltip] = useState<string>('Insert Google Miles');

  const { data: routingSettings } = useGetTenantRoutingSettingsQuery(null);

  useEffect(() => {
    setMilesTooltip(
      `Insert ${routingSettings?.provider === PROVIDER.PC ? 'PC Miler' : 'Google'} Miles`
    );
  }, [routingSettings]);

  const handleGrossPayCalculation = (): void => {
    let grossPay = 0;
    let qboServiceId = grossPayEstimateData?.qbo_service_id;
    let invoiceServiceId = grossPayEstimateData?.invoice_service_id;

    switch (grossPayEstimateData?.unit) {
      case 'PER_MILE':
        grossPay = payRate * (loadedMiles + deadheadMiles);
        break;
      case 'PERCENTAGE':
        if (load_type === LOAD_TYPE.SEGMENT) {
          grossPay = (payRate / 100) * (((loadSegmentRevenue / 100) * loadRate) / driversNumber);
        } else {
          grossPay = (payRate / 100) * (loadRate / driversNumber);
        }

        break;
      case 'PER_HOUR':
        grossPay = payRate * quantity;
        break;
      case 'FLAT':
        grossPay = flatRate;
        break;

      default:
        grossPay = payRate * (loadedMiles + deadheadMiles);
    }

    // Add other pay items to the base pay
    setTotalBaseAndOtherPay(grossPay + otherLoadPayTotal);

    // If prefilled load rate is manually edited, we send service id as null as it was a normal load,
    // regardless of percentage based on default service setting
    if (manuallySetLoadRate) {
      qboServiceId = undefined;
      invoiceServiceId = undefined;
    }

    setGrossPayEstimate(Number(grossPay.toFixed(2)));
    onGrossPayEstimateChange({
      ...grossPayEstimateData,
      rate: payRate,
      quantity: quantity,
      loaded_miles: loadedMiles,
      empty_miles: deadheadMiles,
      trip_revenue_percentage: loadSegmentRevenue,
      overriden_order_rate: loadRate,
      qbo_service_id: qboServiceId,
      invoice_service_id: invoiceServiceId,
      gross_pay_estimate: Number(grossPay.toFixed(2))
    });
  };

  useEffect(() => {
    if (hasBeenReset) {
      setManuallySetLoadRate(false);
      setLoadRate(grossPayEstimateData?.overriden_order_rate ?? 0);
      setLoadedMiles(grossPayEstimateData?.loaded_miles ?? 0);
      setDeadheadMiles(grossPayEstimateData?.empty_miles ?? 0);
      setLoadSegmentRevenue(grossPayEstimateData?.trip_revenue_percentage ?? 0);
      setQuantity(grossPayEstimateData?.quantity ?? 0);
      setPayRate(grossPayEstimateData?.rate ?? 0);
      handleResetValues(false); // set parent state to false
    }
  }, [hasBeenReset]);

  useEffect(() => {
    if (isModalOpen) {
      handleGrossPayCalculation();
    }
  }, [
    payRate,
    loadedMiles,
    deadheadMiles,
    loadSegmentRevenue,
    loadRate,
    quantity,
    flatRate,
    otherLoadPayTotal,
    otherLoadPayItems,
    isModalOpen,
    hasBeenReset
  ]);

  const getByPerMileCalculation = (): JSX.Element => {
    return (
      <Stack spacing={1} direction="row" alignItems="center" justifyContent="space-between">
        <TwField
          inputType={TwInputTypes.number}
          label="Pay rate"
          name="Pay rate"
          startAdorment={
            <Typography variant="body2" fontWeight="400" fontSize="14px" color="#6B7280">
              $
            </Typography>
          }
          value={payRate}
          onInputChange={(event): void => setPayRate(parseFloat(event.target.value))}
        />
        <Typography
          variant="body2"
          fontWeight="400"
          fontSize="14px"
          color="#6B7280"
          sx={{ width: '25px', marginTop: '20px !important' }}
        >
          x (
        </Typography>
        <TwField
          inputType={TwInputTypes.number}
          label="Loaded miles"
          name="Loaded miles"
          value={loadedMiles}
          onInputChange={(event): void => setLoadedMiles(parseFloat(event.target.value))}
          endAdorment={
            !isMileageAutoCalculationDisabled &&
            !enableHideGoogleMapsAndRoutingCalculations && (
              <TwTooltip title={milesTooltip} placement="top">
                <IconButton
                  size="small"
                  onClick={(): void => {
                    if (enableHideGoogleMapsAndRoutingCalculations) return;
                    load_type === LOAD_TYPE.SEGMENT
                      ? recalculateLoadedMilesSplitLoad
                      : recalculateLoadedMiles;
                  }}
                >
                  <CalculatorIcon />
                </IconButton>
              </TwTooltip>
            )
          }
        />
        <Typography
          variant="body2"
          fontWeight="400"
          fontSize="14px"
          color="#6B7280"
          sx={{ marginTop: '20px !important' }}
        >
          +
        </Typography>
        <TwField
          inputType={TwInputTypes.number}
          label="Empty miles"
          name="Empty miles"
          value={deadheadMiles}
          onInputChange={(event): void => setDeadheadMiles(parseFloat(event.target.value) || 0)}
        />
        <Typography
          variant="body2"
          fontWeight="400"
          fontSize="14px"
          color="#6B7280"
          sx={{ marginTop: '20px !important' }}
        >
          )
        </Typography>
      </Stack>
    );
  };

  const getByPercentageCalculation = (): JSX.Element => {
    return (
      <Stack spacing={1} direction="row" alignItems="center" justifyContent="flex-start">
        <TwField
          inputType={TwInputTypes.number}
          sx={{ width: driversNumber > 1 ? '120px' : 'auto' }}
          label="Rate"
          name="Pay rate"
          endAdorment={
            <Typography variant="body2" fontWeight="400" fontSize="14px" color="#6B7280">
              %
            </Typography>
          }
          value={payRate}
          onInputChange={(event): void => setPayRate(parseFloat(event.target.value))}
        />
        <Typography
          variant="body2"
          fontWeight="400"
          fontSize="14px"
          color="#6B7280"
          sx={{ marginTop: '20px !important' }}
        >
          x
        </Typography>
        {driversNumber > 1 && (
          <Typography
            variant="body2"
            fontWeight="400"
            fontSize="14px"
            color="#6B7280"
            sx={{ marginTop: '20px !important' }}
          >
            {'('}
          </Typography>
        )}
        {load_type === LOAD_TYPE.SEGMENT && (
          <>
            <Box sx={{ position: 'relative' }}>
              <TwField
                inputType={TwInputTypes.number}
                label={'Trip %'}
                name="load-segment-revenue"
                value={loadSegmentRevenue}
                onInputChange={(event): void =>
                  setLoadSegmentRevenue(parseFloat(event.target.value))
                }
                endAdorment={
                  <Typography variant="body2" fontWeight="400" fontSize="14px" color="#6B7280">
                    %
                  </Typography>
                }
                sx={{ width: driversNumber > 1 ? '140px' : 'auto' }}
              />
              <TwTooltip placement="top" title={'Segment miles / total order miles'}>
                <InfoIcon
                  sx={{
                    position: 'absolute',
                    top: '2px',
                    right: '55px',
                    width: '16px',
                    color: '#D1D5DB'
                  }}
                />
              </TwTooltip>
            </Box>
            <Typography
              variant="body2"
              fontWeight="400"
              fontSize="14px"
              color="#6B7280"
              sx={{ marginTop: '20px !important' }}
            >
              x
            </Typography>
          </>
        )}

        <Box sx={{ position: 'relative' }}>
          <TwTooltip
            title={
              isDriverPayPercentageBasedOnDefaultService
                ? 'Default service rate / linehaul rate'
                : 'Sum of all service rates on the load'
            }
            placement="top"
          >
            <InfoIcon
              sx={{
                position: 'absolute',
                top: '2px',
                right: '95px',
                width: '16px',
                color: '#D1D5DB'
              }}
            />
          </TwTooltip>
          <TwField
            inputType={TwInputTypes.number}
            label="Load rate"
            name="load-rate"
            value={loadRate}
            onInputChange={(event): void => {
              setLoadRate(parseFloat(event.target.value || '0'));
              setManuallySetLoadRate(true);
            }}
            startAdorment={
              <Typography variant="body2" fontWeight="400" fontSize="14px" color="#6B7280">
                $
              </Typography>
            }
          />
        </Box>

        {driversNumber > 1 && (
          <Typography
            variant="body2"
            fontWeight="400"
            fontSize="14px"
            color="#6B7280"
            sx={{ marginTop: '20px !important' }}
          >
            {(load_type === LOAD_TYPE.SEGMENT ? ')' : '') +
              '/ 2' +
              (load_type === LOAD_TYPE.SEGMENT ? '' : ')')}
          </Typography>
        )}
      </Stack>
    );
  };

  const getByHoursCalculation = (): JSX.Element => {
    return (
      <Stack spacing={1} direction="row" alignItems="center" justifyContent="flex-start">
        <TwField
          inputType={TwInputTypes.number}
          label="Pay rate"
          name="Pay rate"
          startAdorment={
            <Typography variant="body2" fontWeight="400" fontSize="14px" color="#6B7280">
              $
            </Typography>
          }
          value={payRate}
          onInputChange={(event): void => setPayRate(parseFloat(event.target.value))}
        />
        <Typography
          variant="body2"
          fontWeight="400"
          fontSize="14px"
          color="#6B7280"
          sx={{ marginTop: '20px !important' }}
        >
          x
        </Typography>
        <TwField
          inputType={TwInputTypes.number}
          label="Hours"
          name="Hours"
          value={quantity}
          onInputChange={(event): void => setQuantity(parseFloat(event.target.value))}
        />
      </Stack>
    );
  };

  const getFlatCalculation = (): JSX.Element => {
    return (
      <TwField
        inputType={TwInputTypes.number}
        label="Flat rate"
        name="flat-rate"
        value={flatRate}
        onInputChange={(event): void => {
          const value = parseFloat(event.target.value);
          setFlatRate(value);
          setGrossPayEstimate(value);
          setPayRate(value);
          onGrossPayEstimateChange({
            ...grossPayEstimateData,
            gross_pay_estimate: Number(value.toFixed(2))
          });
        }}
      />
    );
  };

  const showCalculationBoxes = (): JSX.Element => {
    switch (grossPayEstimateData?.unit) {
      case 'PER_MILE':
        return getByPerMileCalculation();

      case 'PERCENTAGE':
        return getByPercentageCalculation();

      case 'PER_HOUR':
        return getByHoursCalculation();

      case 'FLAT':
        return getFlatCalculation();

      default:
        return getByPerMileCalculation();
    }
  };

  const recalculateLoadedMiles = (): void => {
    const stops =
      enableLtl && entity === EntityType.TRIP ? [...provider_trip_stops] : [...provider_stops];

    if (stops.length < MIN_STOP_COUNT) return;

    const firstStop = stops.shift();
    const lastStop = stops.pop();

    if (firstStop?.provider_location?.id && lastStop?.provider_location?.id) {
      const intermediateStopsIds: string[] = [];
      stops.map((stop) => {
        if (stop.provider_location?.id) {
          intermediateStopsIds.push(stop.provider_location?.id);
        }
      });

      LoadsService.getEstimatedMilage(
        firstStop?.provider_location?.id,
        lastStop?.provider_location?.id,
        intermediateStopsIds
      ).then((res) => {
        const mileage = Number(parseFloat(res.milage).toFixed(2));
        const miles = NumberUtils.roundNumber(mileage / driversNumber);
        onGrossPayEstimateChange({
          ...grossPayEstimateData,
          gross_pay_estimate: miles * payRate,
          total_loaded_miles: NumberUtils.roundNumber(mileage)
        });
        setLoadedMiles(miles);
      });
    }
  };

  const recalculateLoadedMilesSplitLoad = (): void => {
    if (splitLoadSegment) {
      const segmentStops = [...splitLoadSegment.provider_stops];
      if (segmentStops.length >= MIN_STOP_COUNT) {
        const firstStop = segmentStops.shift();
        const lastStop = segmentStops.pop();

        if (firstStop?.provider_location?.id && lastStop?.provider_location?.id) {
          const intermediateStopsIds: string[] = [];
          segmentStops.map((stop) => {
            if (stop.provider_location?.id) {
              intermediateStopsIds.push(stop.provider_location?.id);
            }
          });

          LoadsService.getEstimatedMilage(
            firstStop.provider_location?.id,
            lastStop.provider_location?.id,
            intermediateStopsIds
          ).then((res) => {
            const mileage = Number(parseFloat(res.milage).toFixed(2));
            if (grossPayEstimateData?.unit === 'PER_MILE') {
              setLoadedMiles(NumberUtils.roundNumber(mileage / driversNumber));
            } else {
              setLoadedMiles(NumberUtils.roundNumber(mileage));
            }
          });
        }
      }
    }
  };

  return (
    <>
      <Stack spacing={1}>{showCalculationBoxes()}</Stack>

      {enableOtherPayInDriverPayModal && (
        <OtherLoadPay
          initialData={otherLoadPayItems}
          onDataUpdate={(newData: Partial<TripDriverOtherPay>[], total: number): void => {
            setOtherLoadPayTotal(total);
            onOtherPayItemsChange(newData as TripDriverOtherPay[]);
          }}
          errors={otherPayItemsErrors}
        />
      )}

      <Stack spacing={1} direction="row" alignItems="center" justifyContent="space-between">
        <Stack spacing={1} direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="body2" fontWeight="600" fontSize="14px">
            Driver Pay Estimate
          </Typography>
        </Stack>

        <Typography variant="body2" fontWeight="600" fontSize="24px">
          <Typography
            variant="body2"
            fontWeight="600"
            color="#6B7280"
            component="span"
            sx={{ marginRight: '5px', fontSize: '24px' }}
          >
            $
          </Typography>
          {grossPayEstimate !== undefined && totalBaseAndOtherPay
            ? parseFloat(`${totalBaseAndOtherPay || 0}`).toFixed(2) || '0.00'
            : '0.00'}
        </Typography>
      </Stack>
    </>
  );
};

export default CalculationBox;
